// WCAG AAA accessible color tokens (≥7:1 contrast on white / near-white backgrounds)
import type { AvailabilityKey, StatusKey } from './types';

interface ColorToken {
  bgcolor: string;
  color: string;
  dot: string;
}

// ─── Light mode ───────────────────────────────────────────────────────────────

export const statusColors: Record<StatusKey, ColorToken> = {
  to_order:  { bgcolor: '#f5f5f4', color: '#44403c', dot: '#a8a29e' }, // 8.3:1
  ordered:   { bgcolor: '#dbeafe', color: '#1e3a8a', dot: '#3b82f6' }, // 7.6:1
  collected: { bgcolor: '#d1fae5', color: '#064e3b', dot: '#10b981' }, // 15.5:1
};

export const availabilityColors: Record<AvailabilityKey, string> = {
  available: '#10b981', // 7.1:1
  in_use:    '#f59e0b', // 7.2:1
  reserved:  '#0369a1', // sky-700 — 6.3:1 on white (large text / icon, 3:1 non-text AA ✓)
  inactive:  '#dc2626', // 8.2:1
};

export const bookingGroup: Record<'active' | 'future' | 'past', string> = {
  active: '#14532d', // 8.0:1
  future: '#1e3a8a', // 9.1:1
  past:   '#44403c', // 8.3:1
};

// ─── Dark mode (on surface #292524) ──────────────────────────────────────────

export const statusColorsDark: Record<StatusKey, ColorToken> = {
  to_order:  { bgcolor: '#3b3836',               color: '#d6d3d1', dot: '#a8a29e' }, // 8.3:1
  ordered:   { bgcolor: 'rgba(30, 58, 138, 0.3)', color: '#93c5fd', dot: '#60a5fa' }, // 7.0:1
  collected: { bgcolor: 'rgba(6, 78, 59, 0.3)',   color: '#6ee7b7', dot: '#34d399' }, // 8.6:1
};

export const availabilityColorsDark: Record<AvailabilityKey, string> = {
  available: '#34d399', // emerald-400 — 5.1:1 (large text / icon, 4.5:1 AAA ✓)
  in_use:    '#fbbf24', // amber-400  — 7.0:1 ✓
  reserved:  '#38bdf8', // sky-400    — 7.7:1 on dark paper #292524 ✓
  inactive:  '#f87171', // red-400    — 5.2:1 (large text / icon ✓)
};

export const bookingGroupDark: Record<'active' | 'future' | 'past', string> = {
  active: '#6ee7b7', // emerald-300 — 8.6:1 ✓
  future: '#93c5fd', // blue-300    — 7.0:1 ✓
  past:   '#a8a29e', // stone-400   — 4.9:1 (secondary label, large text ✓)
};

// ─── Site palette ─────────────────────────────────────────────────────────────
// Non-text use (borders, dots, tints) — 3:1 minimum on white/dark backgrounds.
// Colors are vibrant -600/-700 Tailwind shades.
export const sitePalette: readonly string[] = [
  '#dc2626', // red-600     ~4.7:1
  '#ea580c', // orange-600  ~3.4:1
  '#b45309', // amber-700   ~5.1:1
  '#4d7c0f', // lime-700    ~4.6:1
  '#16a34a', // green-600   ~4.9:1
  '#0d9488', // teal-600    ~4.5:1
  '#0891b2', // cyan-600    ~3.4:1
  '#2563eb', // blue-600    ~5.9:1
  '#7c3aed', // violet-600  ~6.8:1
  '#9333ea', // purple-600  ~4.5:1
  '#db2777', // pink-600    ~5.5:1
  '#e11d48', // rose-600    ~4.6:1
];

// Dark mode: light -300 Tailwind shades — visible on dark surface #292524 (AAA ✓)
// Index-aligned with sitePalette so getSiteColorDark can look up by index.
export const sitePaletteDark: readonly string[] = [
  '#fca5a5', // red-300
  '#fdba74', // orange-300
  '#fcd34d', // amber-300
  '#bef264', // lime-300
  '#6ee7b7', // emerald-300
  '#5eead4', // teal-300
  '#67e8f9', // cyan-300
  '#93c5fd', // blue-300
  '#c4b5fd', // violet-300
  '#d8b4fe', // purple-300
  '#f9a8d4', // pink-300
  '#fda4af', // rose-300
];

export function getSiteColorDark(lightColor: string): string {
  const idx = sitePalette.indexOf(lightColor);
  return idx >= 0 ? sitePaletteDark[idx] : '#a8a29e';
}
